import { useState, useEffect } from 'react';

import { isCompactAdminLayout, isTouchLayout } from './adminLayoutMode';
import { isCoarsePointer } from '../../utils/ui/isCoarsePointer';

const COARSE_QUERY = '(pointer: coarse)';

/**
 * Tracks viewport width and exposes mobile / tablet breakpoint flags (768 / 1024).
 * Used by Admin and Billing shells for consistent layout.
 *
 * Worker and Supervisor dashboards use their own resize logic and additionally treat
 * coarse mobile UA as “mobile” for route/schedule ergonomics (`isMobileDevice()`).
 *
 * KAN-970: SIZES follow touch, LAYOUT follows width. `isMobile` / `isTablet` (width) pick
 * the design; `isTouch` (a finger is the pointer: coarse pointer OR under 1024) picks
 * tap-area sizes, so a sideways iPad (1180 / 1366 wide) still gets finger-sized targets.
 */
export function useResponsiveLayout() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  // iPhone SE-class width (< 360): the bottom bar compacts when the AI button shares its row.
  const [isNarrowPhone, setIsNarrowPhone] = useState(window.innerWidth < 360);

  // A touch laptop that docks a mouse flips this live (the media query changes).
  const [coarse, setCoarse] = useState(() => isCoarsePointer());

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
      setIsNarrowPhone(w < 360);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    let mql;
    try {
      mql = window.matchMedia(COARSE_QUERY);
    } catch {
      return undefined;
    }
    if (!mql) return undefined;
    const sync = () => setCoarse(Boolean(mql.matches));
    sync();
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', sync);
      return () => mql.removeEventListener('change', sync);
    }
    // Older Safari (< 14) only has the deprecated addListener.
    if (typeof mql.addListener === 'function') {
      mql.addListener(sync);
      return () => mql.removeListener(sync);
    }
    return undefined;
  }, []);

  return {
    isMobile,
    isTablet,
    isNarrowPhone,
    /** Prefer cards / touch targets; disable hover-only row chrome (phones + tablets). */
    isCompactAdminLayout: isCompactAdminLayout(isMobile, isTablet),
    /**
     * A finger is the pointer: phone / tablet width OR a coarse pointer (iPad landscape
     * at 1180 / 1366). Use for SIZES only (tap area, button, footer, close button, field
     * text); layout keeps `isMobile` / `isTablet`. KAN-970.
     */
    isTouch: isTouchLayout(isMobile, isTablet, coarse),
  };
}
